import { StrokedText } from "@/components/ui/text/stroked-text";
import cn from "classnames";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { isXsScreen } from "@/utils/screen-size-check";

interface DuelHeaderProps {
  round: number;
  yourScore: number;
  opponentScore: number;
  yourName: string;
  opponentName: string;
  yourCountry?: string | null;
  opponentCountry?: string | null;
  isPlayerA: boolean;
  onQuit?: () => void;
}

export const DuelHeader = ({
  round,
  yourScore,
  opponentScore,
  yourName,
  opponentName,
  yourCountry,
  opponentCountry,
  isPlayerA,
  onQuit,
}: DuelHeaderProps) => {
  const maxScore = 3;

  // Determine which side displays which player based on role
  // Blue (left) = Team A, Red (right) = Team B
  const bluePlayer = isPlayerA
    ? { name: yourName, score: yourScore, country: yourCountry }
    : { name: opponentName, score: opponentScore, country: opponentCountry };
  const redPlayer = isPlayerA
    ? { name: opponentName, score: opponentScore, country: opponentCountry }
    : { name: yourName, score: yourScore, country: yourCountry };

  const renderScoreDots = (score: number, align: "left" | "right") => {
    return (
      <div
        className={cn(
          "flex gap-1",
          align === "left" ? "flex-row" : "flex-row-reverse",
        )}
      >
        {Array.from({ length: maxScore }).map((_, i) => {
          const isFilled = i < score;
          return (
            <div
              key={i}
              className={cn(
                "w-6 h-3 rounded-sm border-2 border-pk-gray-4 shadow-sm",
                isFilled
                  ? align === "left"
                    ? "bg-pk-gray-5 shadow-[0_0_8px_rgba(74,144,226,0.8)]"
                    : "bg-pk-gray-5 shadow-[0_0_8px_rgba(226,74,74,0.8)]"
                  : "bg-pk-gray-1",
              )}
            ></div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="absolute top-0 left-0 w-full h-16 z-50 flex items-center justify-center ">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-transparent h-24" />

      <div className="relative grid grid-cols-2 w-full h-full items-center gap-0  bg-yellow-300">
        {/* Blue Side (Left - Team A) */}
        <div className="flex flex-row gap-2 items-center justify-center  bg-linear-30 from-pk-blue-3 to-blue-500 h-full pr-18 p-1">
          {(onQuit || true) && (
            <SimpleButton
              onClick={
                onQuit ??
                (() => {
                  alert("quit");
                })
              }
              className="h-full px-3 z-60 bg-pink-400"
              variant="red"
            >
              ×
            </SimpleButton>
          )}

          <div className="flex flex-col items-start justify-center h-full w-full">
            <div className="flex items-center gap-2 mb-1">
              <StrokedText
                text={bluePlayer.name}
                fontSize={isXsScreen() ? 12 : 16}
                strokeWidth={3}
                fillColor="#ffffff" // Blue-ish gray
              />
              {bluePlayer.country && (
                <CountryFlag countryCode={bluePlayer.country} size="sm" />
              )}
            </div>
            {renderScoreDots(bluePlayer.score, "left")}
          </div>
        </div>

        {/* Center Round Indicator */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center bg-black/40 px-3 py-1 rounded-lg border border-white/10 backdrop-blur-sm shadow-lg">
          <StrokedText
            text={`ROUND ${round + 1}`}
            fontSize={isXsScreen() ? 14 : 20}
            strokeWidth={4}
            fillColor="#ffcc00" // Gold
            strokeColor="#4a3b00"
          />
          <span className="text-xs">Best of 5</span>
        </div>

        {/* Red Side (Right - Team B) */}
        <div className="flex flex-col items-center justify-center  h-full bg-linear-30 to-pk-red-3 from-red-500 p-2 pl-18">
          <div className="flex items-center gap-2 mb-1">
            {redPlayer.country && (
              <CountryFlag countryCode={redPlayer.country} size="sm" />
            )}
            <StrokedText
              text={redPlayer.name}
              fontSize={isXsScreen() ? 12 : 16}
              strokeWidth={3}
              fillColor="#ffffff" // Red-ish gray
            />
          </div>
          {renderScoreDots(redPlayer.score, "right")}
        </div>
      </div>
    </div>
  );
};
